/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import './App.scss';
import { KewaProvider } from './providers/KewaProvider';
import { ServerException } from './shared/Components';
import Alert from './shared/Components/Alert';
import LogoLoader from './shared/Components/Loader/LogoLoader';
import AppAnalytics from './shared/Config/AppAnalytics';
import { AUTHORITY2 } from './shared/Constants/Constants';
import NetworkDetector from './shared/Utils/NetworkDetector';
import { getAccount } from './state/Account/accountThunks';
import { fetchBrowseNodeMaterials, fetchBrowseNodes } from './state/BrowseNode/browseNodeThunks';
import { fetchAllConfigs } from './state/Config/ConfigsThunks';
import { getPermissionForRole } from './state/Permissions/permissionsThunks';
import { fetchServiceProviders } from './state/ServiceProvider/serviceProviderThunks';
import { serviceWorkerUpdate } from './state/User/userSlice';
import { fetchUsers } from './state/Users/usersThunk';
import './utils/debugCleverTap'; // Import for side effects

const Layout = React.lazy(() => import('./layout/Layout'));
const Auth = React.lazy(() => import('./views/Auth/Auth'));
const FileDownloadPage = React.lazy(() => import('./views/DownloadPage/DownloadPage'));
const ApproveRejectPage = React.lazy(() => import('./views/ApproveReject/ApproveRejectPage'));

const AppWithTracking = () => {
  const account = useSelector((state) => state.account);
  const isServiceWorkerUpdated = useSelector((state) => state.users?.serviceWorkerUpdated);
  const serviceWorkerRegistration = useSelector((state) => state.users?.serviceWorkerRegistration);
  const permissions = useSelector((state) => state.permissions);
  const currentPath = window.location.pathname;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (account?.user?.authorities?.[0]) {
      dispatch(getPermissionForRole(account.user.authorities[0]));
    }
  }, [account?.user?.authorities]);

  useEffect(() => {
    const isTokenSet = !!localStorage.getItem('authenticate_token');
    AppAnalytics.init();
    if (isTokenSet) {
      dispatch(getAccount());
      dispatch(fetchServiceProviders());
      dispatch(fetchBrowseNodes());
      dispatch(fetchUsers());
      dispatch(fetchBrowseNodeMaterials());
      dispatch(fetchAllConfigs());
    }
  }, [!!localStorage.getItem('authenticate_token')]);

  const updateServiceWorker = () => {
    const registrationWaiting = serviceWorkerRegistration?.waiting;
    if (registrationWaiting) {
      registrationWaiting.postMessage({ type: 'SKIP_WAITING' });
      registrationWaiting.addEventListener('statechange', (e) => {
        if (e.target.state === 'activated') {
          window.location.reload();
        }
      });
    }
  };

  const { isAuthenticate, user, isLoading } = account;
  const isTokenSet = !!localStorage.getItem('authenticate_token');
  const showLoader =
    (isTokenSet && !user) ||
    (!!user && !permissions?.permissions?.permissions?.length) ||
    (isTokenSet && !user && isLoading);
  const showSelectCustomer =
    !showLoader && !user?.profile?.id && user?.authorities?.includes(AUTHORITY2.ADMIN);

  useEffect(() => {
    if (isServiceWorkerUpdated) {
      // Show a toast whenever a new build/deployment is available
      console.log('inside isServiceWorkerUpdated', isServiceWorkerUpdated);
      toast.info('A new version of the application is available. Click here to update.', {
        position: 'top-center',
        autoClose: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        onClick: updateServiceWorker,
      });

      localStorage.clear();
      window.location.assign('/login');
    }
  }, [isServiceWorkerUpdated]);

  console.log('outside isServiceWorkerUpdated', isServiceWorkerUpdated);

  const renderContent = () => {
    if (showLoader) {
      return <LogoLoader className="app-loading" />;
    }

    if (currentPath === '/file-download/email-attachment') {
      return <FileDownloadPage />;
    }

    if (currentPath === '/consent-action') {
      return <ApproveRejectPage />;
    }

    if (!isTokenSet) {
      return <Auth />;
    }

    if (isTokenSet) {
      return <Layout />;
    }
  };

  useEffect(() => {
    if (isTokenSet && ['/login'].includes(currentPath)) {
      navigate('/');
    } else if (
      !isTokenSet &&
      ![
        '/login',
        '/register',
        '/reset-password',
        '/create-password',
        '/file-download/email-attachment',
        '/consent-action',
      ].includes(currentPath)
    ) {
      navigate('/login');
    }
  }, [isTokenSet, navigate]);

  return (
    <>
      <div className="App-alert">
        {isServiceWorkerUpdated && (
          <Alert
            text="There is a new version available."
            buttonText="Update"
            dispatchingFunction={serviceWorkerUpdate}
            onClick={updateServiceWorker}
          />
        )}
      </div>
      <ToastContainer position="bottom-right" hideProgressBar autoClose={5000} theme="colored" />
      <ServerException />
      <QueryParamProvider adapter={ReactRouter6Adapter}>
        <React.Suspense fallback={<LogoLoader className="app-loading" />}>
          {renderContent()}
        </React.Suspense>
      </QueryParamProvider>
    </>
  );
};

const App = () => {
  return (
    <KewaProvider>
      <AppWithTracking />
    </KewaProvider>
  );
};

export default NetworkDetector(App);
